import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, AlertTriangle, UserCheck, Wallet, Info } from "lucide-react";
import { ReportDashboardData } from "@/hooks/useReportDashboardStats";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface ReportDashboardViewProps {
  data: ReportDashboardData | null;
  isLoading: boolean;
  startDateTime: Date;
}

const formatTime = (minutes: number, includeSeconds = false): string => {
  if (minutes === 0) return includeSeconds ? "0:00:00" : "00:00";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.round((minutes - Math.floor(minutes)) * 60);
  
  if (includeSeconds) {
    return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const combinedBreach = (l2: number, l3: number) => Math.min(l2 + l3, 100);

interface TATRowProps {
  label: string;
  avgMinutes: number;
  withinTAT: number;
  level1Breach: number;
  level2Breach: number;
  level3Breach: number;
  totalCompleted: number;
  showHours?: boolean;
}

const TATRow = ({ label, avgMinutes, withinTAT, level1Breach, level2Breach, level3Breach, totalCompleted, showHours = false }: TATRowProps) => {
  const hasData = totalCompleted > 0;

  return (
    <div className="py-3 border-b border-border last:border-b-0">
      <div className="text-sm text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {hasData ? (showHours ? formatTime(avgMinutes, true) : formatTime(avgMinutes)) : (showHours ? "--:--:--" : "--:--")}
          </span>
          <span className="text-sm text-muted-foreground">{showHours ? "Avg Discharge" : "mins Avg"}</span>
        </div>
        
        {hasData ? (
          <div className="flex gap-2">
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {withinTAT}%
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {level1Breach}%
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {combinedBreach(level2Breach, level3Breach)}%
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">No data</span>
        )}
      </div>
    </div>
  );
};

export const ReportDashboardView = ({ data, isLoading, startDateTime }: ReportDashboardViewProps) => {
  const endDateTime = new Date(startDateTime);
  endDateTime.setHours(endDateTime.getHours() + 24);

  const formatDateRange = () => {
    const options: Intl.DateTimeFormatOptions = { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return `${startDateTime.toLocaleDateString('en-US', options)} - ${endDateTime.toLocaleDateString('en-US', options)}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 h-full">
              <div className="flex-1 space-y-4">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-7 w-16" />
              </div>
              <div className="flex-1 sm:border-l border-t sm:border-t-0 border-border pt-3 sm:pt-0 sm:pl-4 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-1 h-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-6 w-10" />
                  </div>
                ))}
              </div>
            </div>
          </Card>
          <div className="grid grid-cols-2 gap-3 w-full lg:w-[420px]">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </Card>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                {Array.from({ length: 5 }).map((_, j) => (
                  <div key={j} className="py-3 border-b border-border">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="p-8">
        <p className="text-center text-muted-foreground">No data available for the selected period</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Report Period: <span className="font-medium text-foreground">{formatDateRange()}</span>
      </div>

      {/* Stats Section - Exact Dashboard Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
        {/* Left: Wide grouped discharge card - spans full height */}
        <Card className="p-4 lg:row-span-2">
          <div className="flex flex-col sm:flex-row gap-4 h-full">
            {/* Left half: Discharge counts */}
            <div className="flex-1 flex flex-col justify-center space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4" />
                <span className="text-xs font-medium">Total Discharges Completed</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-foreground">
                  {data.stats.totalDischarges}
                </span>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground mt-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Total Discharge Pending</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-foreground">
                  {data.stats.pendingDischarges}
                </span>
              </div>
            </div>

            {/* Right half: Breakdown */}
            <div className="flex-1 flex flex-col justify-center sm:border-l border-t sm:border-t-0 border-border pt-3 sm:pt-0 sm:pl-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-8 rounded-full bg-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Patients Marked</p>
                    <p className="text-xs text-muted-foreground">For Discharge</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-lg font-semibold text-foreground">
                    {data.extended.patientsMarked}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-8 rounded-full bg-success" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Planned Discharge</p>
                    <p className="text-xs text-muted-foreground">As per LOS</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-lg font-semibold text-foreground">
                    {data.extended.plannedDischarge}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-8 rounded-full bg-warning" />
                  <div>
                    <p className="text-sm font-medium text-foreground">LOS Carry-Over</p>
                    <p className="text-xs text-muted-foreground">From Previous Day</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-lg font-semibold text-foreground">
                    {data.extended.losCarryOver}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Right column top row: Avg Discharge Time + Total Escalations */}
        <div className="grid grid-cols-2 gap-3 w-full lg:w-[420px]">
          <Card className="p-3 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">Avg. Discharge Time</span>
              <Clock className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-2xl font-bold text-foreground">
              {data.stats.avgDischargeTime}
            </span>
          </Card>

          <Card className="p-3 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">Total Escalations</span>
              <AlertTriangle className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-2xl font-bold text-amber-500">
                {data.extended.escalationsL1}
              </span>
              <span className="text-[10px] text-muted-foreground">L1</span>
              <span className="text-2xl font-bold text-red-400">
                {data.extended.escalationsL2}
              </span>
              <span className="text-[10px] text-muted-foreground">L2</span>
              <span className="text-2xl font-bold text-red-700 dark:text-red-600">
                {data.extended.escalationsL3}
              </span>
              <span className="text-[10px] text-muted-foreground">L3</span>
            </div>
          </Card>
        </div>

        {/* Right column bottom row: Patients Admitted + Payment Pending */}
        <div className="grid grid-cols-2 gap-3 w-full lg:w-[420px]">
          <Card className="p-3 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">Patients Admitted</span>
              <UserCheck className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-2xl font-bold text-foreground">
                {data.extended.admittedInPeriod}
              </span>
              <span className="text-[10px] text-muted-foreground">In Period</span>
            </div>
          </Card>

          <Card className="p-3 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">Payment Pending</span>
              <Wallet className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-2xl font-bold text-foreground">
              {data.stats.paymentPending}
            </span>
          </Card>
        </div>
      </div>

      {/* TAT Performance Section - Side by Side like Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Stepwise TAT */}
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              Stepwise TAT Performance
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="mb-2">Average turnaround time for each workflow step.</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Within TAT</span>
                        <span className="text-muted-foreground">≤80% of allowed time</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">L1 Breach</span>
                        <span className="text-muted-foreground">80-100% of allowed time</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Breached</span>
                        <span className="text-muted-foreground">Beyond allowed time</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {data.stepwiseTAT.map((stat) => (
              <TATRow
                key={stat.step}
                label={stat.name}
                avgMinutes={stat.avgMinutes}
                withinTAT={stat.withinTAT}
                level1Breach={stat.level1Breach}
                level2Breach={stat.level2Breach}
                level3Breach={stat.level3Breach}
                totalCompleted={stat.totalCompleted}
              />
            ))}
          </CardContent>
        </Card>

        {/* Ward-wise TAT */}
        <Card className="w-full h-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              Ward wise TAT Performance
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Average discharge turnaround time per ward (from MD to PD).</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Within TAT</span>
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Slightly Over</span>
                      <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Breached</span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {data.wardwiseTAT.length > 0 ? (
              data.wardwiseTAT.map((stat) => (
                <TATRow
                  key={stat.ward}
                  label={stat.ward}
                  avgMinutes={stat.avgMinutes}
                  withinTAT={stat.withinTAT}
                  level1Breach={stat.level1Breach}
                  level2Breach={stat.level2Breach}
                  level3Breach={stat.level3Breach}
                  totalCompleted={stat.totalCompleted}
                  showHours
                />
              ))
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No discharge data available for this period
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Patient Discharge Details */}
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Patient Discharge Details (24-hour cycle)</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {data.patientRows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">S.No</TableHead>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>UHID/Apex ID</TableHead>
                  <TableHead>Discharge Initiated at</TableHead>
                  <TableHead>Total Elapsed time</TableHead>
                  <TableHead>Physical Discharge at</TableHead>
                  <TableHead>Discharge status</TableHead>
                  <TableHead>Payment Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.patientRows.map((row, index) => (
                  <TableRow key={`${row.apexId}-${row.dischargeInitiatedAt}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.patientName}</TableCell>
                    <TableCell>{row.apexId}</TableCell>
                    <TableCell>{row.dischargeInitiatedAt || "-"}</TableCell>
                    <TableCell>{row.totalElapsedTime}</TableCell>
                    <TableCell>{row.physicalDischargeAt || "-"}</TableCell>
                    <TableCell className="capitalize">{row.dischargeStatus}</TableCell>
                    <TableCell className="capitalize">{row.paymentStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No patient discharges initiated in this period
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
